import { RouterRequestType } from "./constants";

const sessionIdHeaderName = "S4D-Session-ID";
const authIdHeaderName = "S4D-Auth-ID";
const csrfIdHeaderName = "S4D-CSRF-ID";
const routerNavigationHeaderName = "S4D-Navigation";

export default class RequestService {
  static #sessionId = "";
  static #authId = "";
  static #csrfId = "";

  /**
   * if the CSRF Token is not known by the request service,
   * sets the CSRF Token to the given value.
   * Returns true if the value was set, otherwise returns false.
   */
  static setCsrfTokenIfEmpty(csrfToken) {
    // Ignore if csrf token is already known
    if (this.#csrfId) {
      return false;
    }

    this.#csrfId = csrfToken;
    return true;
  }

  static navigate(url, data, additionalHeaders) {
    return RequestService.#ajax("GET", url, data, additionalHeaders, true);
  }

  static get(url, data, additionalHeaders) {
    return RequestService.#ajax("GET", url, data, additionalHeaders, false);
  }

  static post(url, data, additionalHeaders) {
    return RequestService.#ajax("POST", url, data, additionalHeaders, false);
  }

  static #ajax(method, url, additionalData, additionalHeaders, navigation = true) {
    let data = this.#getDefaultData();
    data = { ...data, ...additionalData };

    let headers = this.#getRequestHeaders();
    headers = { ...headers, ...additionalHeaders };

    // If this is a navigation request (as opposed to an API request)
    // append the navigation header
    if (navigation) {
      headers[routerNavigationHeaderName] = "RouterNavigation";
    }

    return new Promise((resolve, reject) => {
      $.ajax({
        method, url, data, headers,
      })
        .done((content, status, jqXHR) => {
          this.#processResponseHeaders(jqXHR);

          // Reject non-success responses
          if (jqXHR.status !== 200) {
            const errResp = this.#createErrorResponse(jqXHR);
            reject(errResp);
            return;
          }

          // Success
          resolve(content);
        })
        .fail((jqXHR) => {
          this.#processResponseHeaders(jqXHR);
          const errResp = this.#createErrorResponse(jqXHR);
          reject(errResp);
        });
    });
  }

  /**
   * Creates a stripped down error response without response headers etc.
   * @param jqXHR a jQuery AJAX response object
   * @returns The error response object
   */
  static #createErrorResponse(jqXHR) {
    return {
      status: jqXHR.status,
      statusText: jqXHR.statusText,
      responseText: jqXHR.responseText,
      responseJSON: jqXHR.responseJSON,
      contentType: jqXHR.getResponseHeader("Content-Type")
    }
  }

  static #processResponseHeaders(jqXHR) {
    // Replace ids if provided in the response headers
    RequestService.#sessionId = jqXHR.getResponseHeader(sessionIdHeaderName)
      ?? RequestService.#sessionId;
    RequestService.#authId = jqXHR.getResponseHeader(authIdHeaderName)
      ?? RequestService.#authId;
    RequestService.#csrfId = jqXHR.getResponseHeader(csrfIdHeaderName)
      ?? RequestService.#csrfId;

    // AJAX redirects are handled manually, so they receive a 200 "OK" status
    // and the location is given in the response headers.
    // Convert these responses back to 302 redirect responses internally 
    // so they can be distinguished in request error handlers.
    const location = jqXHR.getResponseHeader("location");
    if (location) {
      if (jqXHR.status === 200) {
        jqXHR.status = 302;
        jqXHR.statusText = "Found";
      }

      // Include the location in any case
      jqXHR.responseJSON = {
        location
      };
    }
  }

  static #getDefaultData() {
    return {
      r: RouterRequestType.Standard
    }
  }

  static #getRequestHeaders() {
    const headers = {};

    if (RequestService.#sessionId) {
      headers[sessionIdHeaderName] = RequestService.#sessionId;
    }
    if (RequestService.#authId) {
      headers[authIdHeaderName] = RequestService.#authId;
    }
    if (RequestService.#csrfId) {
      headers[csrfIdHeaderName] = RequestService.#csrfId;
    }

    return headers;
  }
}
